/**
 * LayoutRow (abstract) — role base for rendering-container rows
 * (canvas, column, timeline[row], timeline[column], grid, group,
 * block, header, label, timeline-legend).
 *
 * Reads/writes its slice of `sheet.stores.rowMetadata` for the
 * layout-specific fields (`filter`, `canvas`). Concrete subclasses
 * narrow via the descriptor's `layout` field.
 */

import type { LayoutDescriptor } from "../descriptor.ts";
import type { RowMetadata } from "../../stores/row-metadata-store.ts";
import type { RowLocalId } from "../../ids/row-local-id.ts";
import type { ModelSheet } from "../../sheet/rows-sheet.ts";
import { sheetOf, sheetStores } from "../../sheet/sheet-stores.ts";
import { ModelRow } from "../model-row.ts";

/**
 * The base of a row that lays out its children (a timeline, a grid, a
 * canvas).
 */
export abstract class LayoutRow extends ModelRow {
  declare readonly descriptor: LayoutDescriptor;

  constructor(id: RowLocalId, descriptor: LayoutDescriptor, sheet: ModelSheet) {
    super(id, descriptor, sheet);
  }

  /** Current row metadata (label / heightPx / filter / canvas / attributes). */
  config(): RowMetadata {
    return sheetStores(sheetOf(this)).rowMetadata.get(this.id);
  }

  override toDebug(): Record<string, unknown> {
    return {
      ...super.toDebug(),
      layout: this.descriptor.layout,
    };
  }
}
